// frontend/src/pages/PaymentSuccess.jsx
// ✅ Payment success page - shows after Stripe checkout

import React from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Screen from "../uizard/ui/Screen";
import { apiGet } from "../lib/api";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");

  const [loading, setLoading] = React.useState(true);
  const [session, setSession] = React.useState(null);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!sessionId) {
      setError("No session ID found");
      setLoading(false);
      return;
    }

    let alive = true;

    (async () => {
      try {
        const data = await apiGet(`/api/stripe/session/${sessionId}`);
        if (alive) {
          setSession(data);
        }
      } catch (err) {
        if (alive) {
          setError(err?.message || "Failed to load session");
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [sessionId]);

  const creditsAdded = session?.metadata?.credits || 0;
  const amountPaid = session?.amountTotal ? (session.amountTotal / 100).toFixed(2) : "0.00";

  return (
    <Screen>
      <style>{`
        .success-container {
          max-width: 600px;
          margin: 40px auto;
          text-align: center;
        }

        .success-icon {
          font-size: 80px;
          margin-bottom: 20px;
          animation: bounce 0.6s ease;
        }

        @keyframes bounce {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        .success-title {
          font-size: 32px;
          font-weight: 800;
          background: linear-gradient(135deg, #10B981 0%, #059669 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 16px;
        }

        .success-message {
          font-size: 18px;
          color: #6B7280;
          margin-bottom: 32px;
        }

        .success-details {
          background: linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 100%);
          border: 1px solid #BBF7D0;
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 32px;
        }

        .success-detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #E5E7EB;
        }

        .success-detail-row:last-child {
          border-bottom: none;
        }

        .success-detail-label {
          font-size: 14px;
          color: #6B7280;
          font-weight: 600;
        }

        .success-detail-value {
          font-size: 18px;
          font-weight: 800;
          color: #10B981;
        }

        .success-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .btn {
          height: 48px;
          padding: 0 24px;
          border-radius: 12px;
          font-weight: 700;
          font-size: 16px;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          border: 1px solid #E5E7EB;
          background: white;
          color: #111827;
        }

        .btn-primary {
          background: #6D5EF9;
          color: white;
          border-color: #6D5EF9;
        }

        .btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .btn-primary:hover {
          background: #5B4EE8;
        }

        .error-container {
          max-width: 600px;
          margin: 40px auto;
          text-align: center;
          padding: 32px;
          background: #FEF2F2;
          border: 1px solid #FEE2E2;
          border-radius: 16px;
        }

        .error-icon {
          font-size: 64px;
          margin-bottom: 16px;
        }

        .skeleton {
          height: 400px;
          background: linear-gradient(90deg, #f5f5f7, #ececf2, #f5f5f7);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 16px;
        }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {loading && (
        <div className="success-container">
          <div className="skeleton" />
        </div>
      )}

      {!loading && error && (
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Unable to Verify Payment</h2>
          <p>{error}</p>
          <div className="success-actions">
            <Link to="/profile" className="btn btn-primary">
              Go to Profile
            </Link>
          </div>
        </div>
      )}

      {!loading && !error && session && (
        <div className="success-container">
          <div className="success-icon">✅</div>
          <h1 className="success-title">Payment Successful!</h1>
          <p className="success-message">
            Your credits have been added to your account.
          </p>

          <div className="success-details">
            <div className="success-detail-row">
              <span className="success-detail-label">Credits Added</span>
              <span className="success-detail-value">+{creditsAdded}</span>
            </div>
            <div className="success-detail-row">
              <span className="success-detail-label">Amount Paid</span>
              <span className="success-detail-value">${amountPaid}</span>
            </div>
            <div className="success-detail-row">
              <span className="success-detail-label">Status</span>
              <span className="success-detail-value">
                {session.status === "paid" ? "Completed" : session.status}
              </span>
            </div>
          </div>

          <div className="success-actions">
            <Link to="/create" className="btn btn-primary">
              Start Creating
            </Link>
            <Link to="/profile" className="btn">
              View Profile
            </Link>
          </div>
        </div>
      )}
    </Screen>
  );
}